import React from 'react';
import { Box, Chip, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { BiMap, BiPhone, BiCar, BiX, BiTime } from 'react-icons/bi';
import type { ClickedRunStop } from '../runTypes';
import { RUN_STOP_COLORS } from '../runTypes';

interface RunStopInfoPopupProps {
  clickedStop: ClickedRunStop;
  onClose: () => void;
}

const formatStatus = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const RunStopInfoPopup: React.FC<RunStopInfoPopupProps> = ({ clickedStop, onClose }) => {
  const theme = useTheme();
  const { stop, screenX, screenY } = clickedStop;

  // Position the popup near the click, clamped to viewport
  const left = Math.min(screenX, window.innerWidth - 300);
  const top = Math.min(screenY + 12, window.innerHeight - 260);

  return (
    <Box
      sx={{
        position: 'fixed',
        left,
        top,
        zIndex: 1500,
        width: 280,
        bgcolor: 'background.paper',
        borderRadius: 2,
        boxShadow: 6,
        border: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1, bgcolor: 'grey.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: RUN_STOP_COLORS[stop.phase],
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {stop.stopOrder}
          </Box>
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            {stop.customerName || `Stop ${stop.stopOrder}`}
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose}>
          <BiX size={16} />
        </IconButton>
      </Box>

      <Divider />

      {/* Details */}
      <Box sx={{ px: 1.5, py: 1.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Chip
          label={formatStatus(stop.status)}
          size='small'
          sx={{
            fontWeight: 600,
            fontSize: 10,
            height: 20,
            alignSelf: 'flex-start',
            bgcolor: RUN_STOP_COLORS[stop.phase] + '20',
            color: RUN_STOP_COLORS[stop.phase],
            border: `1px solid ${RUN_STOP_COLORS[stop.phase]}40`,
          }}
        />

        {stop.address ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <BiMap size={14} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography variant='caption' color='text.secondary'>{stop.address}</Typography>
          </Box>
        ) : null}

        {stop.customerPhone ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BiPhone size={14} color={theme.palette.text.secondary} />
            <Typography variant='caption' color='text.secondary'>{stop.customerPhone}</Typography>
          </Box>
        ) : null}

        {stop.serviceDescription ? (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <BiCar size={14} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
            <Typography variant='caption' color='text.secondary' sx={{ fontStyle: 'italic' }}>
              {stop.serviceDescription}
            </Typography>
          </Box>
        ) : null}

        {stop.estimatedServiceMinutes ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <BiTime size={14} color={theme.palette.text.secondary} />
            <Typography variant='caption' color='text.secondary'>
              Est. ~{stop.estimatedServiceMinutes} min
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default RunStopInfoPopup;
